import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { horodatageCourt } from '../lib/format'
import { Superpose } from '../components/Superpose'
import type { Profil, RoleGlobal } from '../lib/types'

/**
 * Gestion des comptes (superadmin seulement) : rôle de chacun, activation et
 * désactivation, journal d'audit. Les règles sont aussi appliquées par la base
 * (migration 014b) : cet écran n'est qu'une façade.
 */
const ROLES: { cle: RoleGlobal; libelle: string; aide: string }[] = [
  { cle: 'consultation', libelle: 'Consultation', aide: 'Lecture seule des chantiers où il est assigné' },
  { cle: 'chef', libelle: "Chef d'équipe", aide: 'Photos, annotations, suivis sur ses chantiers' },
  { cle: 'admin', libelle: 'Administrateur', aide: 'Tous les chantiers, plans, devis, équipe' },
  { cle: 'superadmin', libelle: 'Superadmin', aide: 'Admin + rôles, comptes, corbeille, journal' }
]

interface Entree {
  id: number
  quand: string
  action: string
  cible: string | null
  details: Record<string, unknown> | null
  auteur: { nom: string } | null
}

const ACTIONS: Record<string, string> = {
  role: 'Rôle changé',
  compte: 'Compte',
  inscription: 'Nouvelle inscription',
  suppression_definitive: 'Suppression définitive',
  statut_chantier: 'Statut du chantier',
  invitation: 'Invitation envoyée',
  lien_mot_de_passe: 'Lien de mot de passe envoyé'
}

const STATUTS: Record<string, string> = { actif: 'actif', suspendu: 'suspendu', termine: 'archivé' }

function resume(e: Entree): string {
  const d = e.details ?? {}
  const role = (r: unknown) => ROLES.find((x) => x.cle === r)?.libelle ?? String(r)
  if (e.action === 'role') return `${role(d.role_avant)} → ${role(d.role_apres)}`
  if (e.action === 'compte') return d.actif_apres ? 'Activé' : 'Désactivé'
  if (e.action === 'inscription') return 'En attente d’activation'
  if (e.action === 'statut_chantier') return `${STATUTS[String(d.avant)] ?? d.avant} → ${STATUTS[String(d.apres)] ?? d.apres}`
  if (e.action === 'invitation') return `Rôle : ${role(d.role)}`
  if (e.action === 'suppression_definitive') return [d.chantier, d.type].filter(Boolean).join(' · ')
  return ''
}

export function Utilisateurs({ fermer }: { fermer: () => void }) {
  const { profil } = useAuth()
  const [vue, setVue] = useState<'comptes' | 'journal'>('comptes')
  const [comptes, setComptes] = useState<Profil[] | null>(null)
  const [journal, setJournal] = useState<Entree[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)
  const [invitation, setInvitation] = useState(false)
  const [invNom, setInvNom] = useState('')
  const [invCourriel, setInvCourriel] = useState('')
  const [invRole, setInvRole] = useState<RoleGlobal>('chef')

  const charger = useCallback(async () => {
    setErreur(null)
    const { data, error } = await supabase
      .from('profils')
      .select('id, nom, courriel, role, actif')
      .order('actif')
      .order('nom')
    if (error) setErreur(error.message)
    else setComptes((data as Profil[]) ?? [])
    const { data: j, error: e2 } = await supabase
      .from('journal_audit')
      .select('id, quand, action, cible, details, auteur:profils!journal_audit_auteur_fkey(nom)')
      .order('quand', { ascending: false })
      .limit(200)
    if (e2) setErreur(e2.message)
    else setJournal((j as unknown as Entree[]) ?? [])
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if (e.key === 'Escape') fermer()
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [fermer])

  async function modifier(p: Profil, changements: Partial<Pick<Profil, 'role' | 'actif'>>, question?: string) {
    if (question && !window.confirm(question)) return
    setOccupe(p.id)
    setErreur(null)
    setSucces(null)
    const { error } = await supabase.from('profils').update(changements).eq('id', p.id)
    if (error) setErreur(error.message)
    await charger()
    setOccupe(null)
  }

  /** Appel de la fonction serveur « utilisateurs » (invitation, lien de mot de passe). */
  async function serveur(corps: Record<string, unknown>): Promise<boolean> {
    const { data, error } = await supabase.functions.invoke('utilisateurs', { body: corps })
    if (error) {
      let m = error.message
      try {
        const ctx = (error as { context?: Response }).context
        const j = ctx ? await ctx.json() : null
        if (j?.erreur) m = j.erreur
      } catch {
        /* réponse non JSON */
      }
      if (/Failed to send a request|Failed to fetch/i.test(m))
        m = "Fonction serveur « utilisateurs » injoignable (pas de réseau, ou pas encore déployée)."
      setErreur(m)
      return false
    }
    if (data?.erreur) {
      setErreur(String(data.erreur))
      return false
    }
    return true
  }

  async function inviter(e: FormEvent) {
    e.preventDefault()
    setErreur(null)
    setSucces(null)
    setOccupe('invitation')
    const ok = await serveur({ action: 'inviter', nom: invNom.trim(), courriel: invCourriel.trim(), role: invRole })
    setOccupe(null)
    if (!ok) return
    setSucces(`Invitation envoyée à ${invCourriel.trim()}. Ajoute maintenant la personne à ses chantiers (onglet Info du chantier).`)
    setInvitation(false)
    setInvNom('')
    setInvCourriel('')
    await charger()
  }

  async function envoyerLien(p: Profil) {
    if (!window.confirm(`Envoyer à ${p.courriel} un lien pour choisir un nouveau mot de passe ?`)) return
    setErreur(null)
    setSucces(null)
    setOccupe(p.id)
    const ok = await serveur({ action: 'lien', id: p.id })
    setOccupe(null)
    if (ok) {
      setSucces(`Lien envoyé à ${p.courriel}.`)
      await charger()
    }
  }

  const enAttente = (comptes ?? []).filter((p) => !p.actif)
  const actifs = (comptes ?? []).filter((p) => p.actif)

  function Carte({ p }: { p: Profil }) {
    const moi = p.id === profil?.id
    return (
      <div className="carte bande-nulle" style={{ opacity: p.actif ? 1 : 0.85 }}>
        <div className="entre">
          <div style={{ minWidth: 0 }}>
            <p className="titre">
              {p.nom || '(sans nom)'}
              {moi && <span className="code"> · toi</span>}
            </p>
            <p className="meta">
              <span>{p.courriel}</span>
            </p>
          </div>
          {!p.actif && <span className="pastille p-alerte">Désactivé</span>}
        </div>
        <label className="champ" style={{ marginTop: 10, marginBottom: 6 }}>
          <span>Rôle</span>
          <select
            value={p.role}
            disabled={occupe !== null}
            onChange={(e) => {
              const r = e.target.value as RoleGlobal
              const libelle = ROLES.find((x) => x.cle === r)?.libelle
              void modifier(
                p,
                { role: r },
                r === 'superadmin' || r === 'admin' || (moi && r !== p.role)
                  ? `Donner le rôle « ${libelle} » à ${p.nom} ?`
                  : undefined
              )
            }}
          >
            {ROLES.map((r) => (
              <option key={r.cle} value={r.cle}>
                {r.libelle} — {r.aide}
              </option>
            ))}
          </select>
        </label>
        {!moi && (
          <button
            className={`bouton ${p.actif ? 'creux' : ''}`}
            disabled={occupe !== null}
            onClick={() =>
              void modifier(
                p,
                { actif: !p.actif },
                p.actif
                  ? `Désactiver le compte de ${p.nom} ? Il n'aura plus accès à rien, même sur un téléphone déjà connecté.`
                  : undefined
              )
            }
          >
            {occupe === p.id ? '…' : p.actif ? 'Désactiver le compte' : 'Activer le compte'}
          </button>
        )}
        {p.actif && (
          <button
            className="bouton creux"
            style={{ marginTop: 8 }}
            disabled={occupe !== null}
            onClick={() => void envoyerLien(p)}
          >
            Envoyer un lien de mot de passe
          </button>
        )}
      </div>
    )
  }

  return (
    <Superpose className="feuille">
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Chantiers
          </button>
          <div style={{ flex: 1 }}>
            <h1>Utilisateurs</h1>
            <p className="sous">Réservé au superadmin</p>
          </div>
          <button className="fermer-x" onClick={fermer} aria-label="Fermer">
            ✕
          </button>
        </div>
      </header>
      <div className="sous-onglets" role="tablist">
        <button role="tab" aria-selected={vue === 'comptes'} onClick={() => setVue('comptes')}>
          Comptes
          {enAttente.length > 0 && <span className="compte alerte">{enAttente.length}</span>}
        </button>
        <button role="tab" aria-selected={vue === 'journal'} onClick={() => setVue('journal')}>
          Journal
        </button>
      </div>
      <div className="corps">
        {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}

        {succes && <p className="avis" style={{ marginBottom: 12 }}>{succes}</p>}

        {vue === 'comptes' && (
          <>
            {!invitation ? (
              <button className="bouton" style={{ marginBottom: 14 }} onClick={() => setInvitation(true)}>
                + Inviter un utilisateur
              </button>
            ) : (
              <form className="carte bande-nulle" style={{ marginBottom: 14 }} onSubmit={inviter}>
                <p className="titre" style={{ marginBottom: 8 }}>Inviter un utilisateur</p>
                <label className="champ">
                  <span>Nom complet</span>
                  <input value={invNom} onChange={(e) => setInvNom(e.target.value)} required autoComplete="off" />
                </label>
                <label className="champ">
                  <span>Courriel</span>
                  <input
                    type="email"
                    inputMode="email"
                    value={invCourriel}
                    onChange={(e) => setInvCourriel(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </label>
                <label className="champ">
                  <span>Rôle</span>
                  <select value={invRole} onChange={(e) => setInvRole(e.target.value as RoleGlobal)}>
                    {ROLES.map((r) => (
                      <option key={r.cle} value={r.cle}>
                        {r.libelle} — {r.aide}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="code" style={{ marginBottom: 10 }}>
                  La personne reçoit un courriel avec un lien pour choisir son mot de passe. Le compte est actif
                  tout de suite avec ce rôle.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="bouton" disabled={occupe !== null}>
                    {occupe === 'invitation' ? 'Envoi…' : 'Envoyer l’invitation'}
                  </button>
                  <button type="button" className="bouton creux" onClick={() => setInvitation(false)}>
                    Annuler
                  </button>
                </div>
              </form>
            )}
            {comptes === null && <p className="vide">Chargement…</p>}
            {enAttente.length > 0 && (
              <>
                <div className="sect">Désactivés ou en attente d'activation</div>
                <p className="code" style={{ marginBottom: 8 }}>
                  Un nouveau compte arrive désactivé. Choisis son rôle, puis active-le. Ajoute-le ensuite à ses
                  chantiers (onglet Info du chantier).
                </p>
                <div className="liste">
                  {enAttente.map((p) => (
                    <Carte key={p.id} p={p} />
                  ))}
                </div>
              </>
            )}
            {actifs.length > 0 && (
              <>
                <div className="sect">Comptes actifs</div>
                <div className="liste">
                  {actifs.map((p) => (
                    <Carte key={p.id} p={p} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {vue === 'journal' && (
          <>
            {journal === null && <p className="vide">Chargement…</p>}
            {journal?.length === 0 && <p className="vide">Rien au journal pour l'instant.</p>}
            <div className="liste">
              {(journal ?? []).map((e) => (
                <div key={e.id} className="carte bande-nulle">
                  <p className="code">
                    {horodatageCourt(e.quand)} · {e.auteur?.nom ?? 'système'}
                  </p>
                  <p className="titre">
                    {ACTIONS[e.action] ?? e.action}
                    {e.cible ? ` — ${e.cible}` : ''}
                  </p>
                  <p className="meta">
                    <span>{resume(e)}</span>
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Superpose>
  )
}
